import * as React from "react"

import { cn } from "../../cn"
import { CopyableId } from "../CopyableId/CopyableId"
import { normalizeResourceCopyTargets } from "../ResourceMenu/ResourceMenu"
import type { ColumnDef, RowCopyTargets } from "./types"

export const DEFAULT_COLUMN_WIDTH = 150
export const SKELETON_ROW_COUNT = 8
/** Estimated row height for the virtualizer (extracted from VirtualRunsTable). */
export const ESTIMATED_ROW_HEIGHT = 52
/** Render this many rows beyond the viewport on each side. */
export const OVERSCAN = 100
/** Fetch the next page once the viewport is within this many rows of the end. */
export const LOAD_MORE_ROW_THRESHOLD = 15
/** Fixed width (px) of the leading checkbox column when `selectable`. */
export const SELECT_COLUMN_WIDTH = 40
/** Fixed width (px) of the trailing row-actions column when `rowActions` is set. */
export const ACTION_COLUMN_WIDTH = 48
/** Left indent (px) per nesting depth for expandable rows. */
export const NEST_INDENT = 16
/**
 * Outer gutter on the table's left/right edges so the first/last column line up
 * with the page header (`px-6`). Applied as horizontal padding on each row's
 * grid container, while the row dividers stay full-bleed (the border wraps the
 * padded box; only the grid tracks inset). Sticky offsets add `EDGE_GUTTER_PX`
 * so pinned / selection / action columns stay glued to the gutter during
 * horizontal scroll.
 *
 * Uses an explicit px value (not `px-3`) so it stays exactly `EDGE_GUTTER_PX`
 * real pixels. The table's geometry is raw px (grid tracks + sticky offsets),
 * but the app sets `html { font-size: 0.9rem }`, under which rem-based spacing
 * (`px-3` = 0.75rem ≈ 10.8px) would fall ~1px short of the 12px offsets — the
 * pinned/selection cells would then sit just inside the gutter cover and let
 * scrolling content peek through at the edge.
 *
 * The width is read from the `--table-edge-gutter` CSS variable (set on the
 * root, defaulting to `EDGE_GUTTER_PX`) so `edgeToEdge` can collapse it to zero
 * for tables embedded in an already-padded container. Sticky-column offsets
 * can't reference the variable (they're numeric inline styles asserted in px by
 * tests), so those thread the resolved `edgeGutterPx` value instead.
 */
export const EDGE_GUTTER_PX = 12
// Reads `--table-edge-gutter` (set on the root) with a 12px fallback. The var
// reference is a literal so Tailwind's JIT scanner still emits the class.
export const EDGE_GUTTER_CLASS = "px-[var(--table-edge-gutter,12px)]"
// `edgeToEdge` companion to the zeroed gutter: every cell carries its own `px-3`,
// so with the outer gutter gone the first / last column would still sit ~12px in
// from the edge. Drop just the outer cell's leading / trailing padding (the row
// grid's first / last child) so the columns sit flush; inter-column padding
// between cells is untouched. Skipped in `cardRows` mode: platters keep their
// cell padding so text stays inset from the platter border rather than flush.
export const EDGE_TO_EDGE_ROW_CLASS =
  "[&>*:first-child]:pl-0 [&>*:last-child]:pr-0"

/**
 * Bottom divider for an opaque sticky cell (pinned / selection / actions). These
 * cells sit at `z-index: 1` over the row, so they need to carry the row's bottom
 * divider themselves. A `box-shadow` (drawn 1px *outside* the box) is used rather
 * than `border-b` (drawn 1px *inside*, at the content-box edge): the row's own
 * `border-b` sits just below the content box, so a cell `border-b` lands 1px above
 * it and shows as a double line, while the shadow lands exactly on it. It also
 * survives the GPU-composited clip that erases a nested-sticky header cell's
 * bottom border (the header rowgroup is sticky and pinned cells are sticky within
 * it), which a `border-b` does not.
 */
export const STICKY_CELL_DIVIDER = "shadow-[0_1px_0_0_var(--coval-border)]"

/** Shared empty selection so the `selectedIds`-omitted case keeps a stable ref. */
export const EMPTY_SELECTION: ReadonlySet<string> = new Set()

/** Font conventions, keyed by column variant. */
export const VARIANT_CLASSES = {
  id: "font-mono text-coval-text-muted",
  name: "font-semibold text-coval-text",
  default: "text-coval-text",
} as const

export function variantClasses(variant: ColumnDef<unknown>["variant"]): string {
  return VARIANT_CLASSES[variant ?? "default"]
}

export type ColumnAlign = "left" | "right" | "center"

/**
 * Resolve a column's horizontal alignment. ID columns are right-aligned by
 * default; an explicit `align` still wins for any column.
 */
export function columnAlign<T>(col: ColumnDef<T> | undefined): ColumnAlign {
  if (col?.align) {
    return col.align
  }
  return col?.variant === "id" ? "right" : "left"
}

/** Flex/text classes for a resolved column alignment (`left` needs none). */
export function alignClasses(align: ColumnAlign): string {
  if (align === "right") {
    return "justify-end text-right"
  }
  if (align === "center") {
    return "justify-center text-center"
  }
  return ""
}

/**
 * Interactive elements a row may contain (CopyableId, links, future row
 * actions). Clicks / activations originating inside one of these should drive
 * that control, not row navigation.
 */
export const INTERACTIVE_DESCENDANT_SELECTOR =
  'a, button, input, select, textarea, [role="button"], [role="link"], [role="menuitem"], [role="checkbox"], [data-prevent-row-click="true"]'

/**
 * True unless the event started inside an interactive descendant of the row.
 * Checks `Element` (not `HTMLElement`) so clicks landing on an `svg`/`path`
 * inside an icon-only button or link are still attributed to that control.
 */
export function shouldNavigateRow(target: EventTarget | null): boolean {
  return !(
    target instanceof Element && target.closest(INTERACTIVE_DESCENDANT_SELECTOR)
  )
}

/**
 * True when a right-click inside a row belongs to the row's actions menu rather
 * than to the browser's own. An interactive control keeps its native menu, and
 * so does a selection the click lands inside, where the browser offers Copy.
 *
 * Checked on the way down, because the row's context-menu trigger is merged onto
 * the row element itself: a handler there runs alongside the trigger instead of
 * instead of it, so the event has to be stopped before it arrives.
 */
export function shouldOpenRowContextMenu(target: EventTarget | null): boolean {
  if (!shouldNavigateRow(target)) {
    return false
  }
  if (!(target instanceof Element)) {
    return true
  }
  const selection = window.getSelection()
  if (!selection || selection.isCollapsed || !selection.anchorNode) {
    return true
  }
  const row = target.closest('[role="row"]')
  return !row?.contains(selection.anchorNode)
}

/**
 * Resolves a row's copy group. A path is made absolute against the current
 * origin, so what lands on the clipboard is shareable rather than relative to
 * wherever it was copied from.
 */
export function resolveRowCopyTargets<T>({
  row,
  rowId,
  rowCopyId,
  rowHref,
}: {
  row: T
  rowId: string
  rowCopyId: boolean | ((row: T) => string | undefined) | undefined
  rowHref: ((row: T) => string | undefined) | undefined
}): RowCopyTargets {
  const id =
    rowCopyId === true
      ? rowId
      : typeof rowCopyId === "function"
        ? rowCopyId(row)
        : undefined
  const href = rowHref?.(row)
  return normalizeResourceCopyTargets({ id, url: href })
}

/** Stringify primitive cell values; objects/nullish render empty. */
export function renderFallback(value: unknown): React.ReactNode {
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value)
  }
  return ""
}

/**
 * Default renderer for `id` columns: the standardized `CopyableId`
 * cell — 4-char display, copy icon on hover, click copies the full id. A string
 * value is required; anything else falls back to plain rendering.
 */
export function renderId(value: unknown): React.ReactNode {
  if (typeof value === "string") {
    return <CopyableId id={value} />
  }
  return renderFallback(value)
}

/**
 * Sticky-positioning facts for a pinned column, keyed by column id.
 * `offset` is the px distance from the pinned edge (it accumulates the widths
 * of the columns nearer that edge, plus the selection column on the left).
 * `edge` marks the column at the inner boundary of its group — it carries the
 * divider that separates the pinned block from the scrolling center. `outer`
 * marks the column at the outer (screen-edge) boundary of its group — it paints
 * the gutter cover (see `gutterCoverClasses`). A selection / actions column,
 * when present, is the outermost sticky cell, so the adjacent pinned column is
 * not `outer`.
 */
export interface PinMeta {
  side: "left" | "right"
  offset: number
  edge: boolean
  outer: boolean
}

/** Inline sticky offset for a pinned cell; `undefined` leaves the cell in flow. */
export function pinnedCellStyle(
  pin: PinMeta | undefined,
): React.CSSProperties | undefined {
  if (!pin) {
    return undefined
  }
  return pin.side === "left"
    ? { position: "sticky", left: pin.offset, zIndex: 1 }
    : { position: "sticky", right: pin.offset, zIndex: 1 }
}

/**
 * Background + divider classes for a pinned cell. `self-stretch` makes the cell
 * fill the full row height (the row centers its cells, so without it the cell
 * sizes to its content) — this keeps the opaque background covering the whole
 * row so scrolling center cells can't bleed through, and makes the inner-edge
 * divider a single contiguous line down the column. The background tracks the
 * row's hover / selected state.
 */
/**
 * Background a highlighted row and its opaque cells take. A selected row gets
 * the blue selection wash; a row that is merely active — the keyboard cursor —
 * keeps the neutral step it has always had, so moving the cursor never reads as
 * picking a row. Platter rows step to `surface-default-hover` rather than to
 * the contrast surface they already sit on. Written out per branch so Tailwind
 * sees the literal class names.
 */
export function rowHighlightClass({
  selected,
  active,
  cardRows,
}: {
  selected?: boolean
  active?: boolean
  cardRows?: boolean
}): string | undefined {
  if (selected) {
    return "bg-surface-selected"
  }
  if (active) {
    return cardRows ? "bg-surface-default-hover" : "bg-surface-contrast"
  }
  return undefined
}

export function pinnedCellClasses(
  pin: PinMeta,
  opts: {
    selected?: boolean
    /** Keyboard/collection cursor, which highlights without being a pick. */
    active?: boolean
    warning?: boolean
    /** Whether this cell tints when the row is hovered (see `rowHover`). */
    hoverable?: boolean
    scrolledX?: boolean
    canScrollRight?: boolean
    cardRows?: boolean
    // A platter table's top header row, which floats on the contrast surface
    // outside the platters rather than sitting on one. Its pinned cells take
    // that surface and drop the divider, like the header row itself — painting
    // the default surface there leaves a lit block behind the pinned headers.
    platterHeader?: boolean
  } = {},
): string {
  // Fade whenever center content actually sits under this edge: under a left
  // edge once scrolled from the start, under a right edge whenever content is
  // still hidden past the right (true at rest for an overflowing table).
  const contentUnderEdge =
    pin.side === "left" ? opts.scrolledX : opts.canScrollRight
  return cn(
    opts.platterHeader
      ? "self-stretch bg-surface-contrast"
      : // The opaque cell carries the row's bottom divider (see
        // STICKY_CELL_DIVIDER).
        cn("self-stretch bg-surface-default", STICKY_CELL_DIVIDER),
    // An opaque pinned cell has to track the row it is part of, or it reads as
    // a differently-colored block while the row is highlighted.
    rowHighlightClass(opts),
    opts.warning && "bg-coval-warning-surface text-coval-warning-on-surface",
    // Hover matches the row's (see `BodyRow`).
    opts.hoverable && "group-hover:bg-surface-contrast",
    opts.warning && opts.hoverable && "group-hover:bg-coval-warning-surface",
    // Scrolling center content fades into the pinned column's inner edge.
    pin.edge && contentUnderEdge && edgeFadeClasses(pin.side),
    pin.outer && gutterCoverClasses(pin.side, { divider: !opts.platterHeader }),
  )
}

/**
 * Fades scrolling center content into a pinned column's inner edge: a strip just
 * outside that edge, filled with the cell's own background (`bg-inherit`, so it
 * tracks the row's hover / selected state exactly like `gutterCoverClasses`) and
 * masked to a linear gradient — opaque against the pinned cell, transparent away
 * from it. Text dissolves into the pinned column as it scrolls under. Uses
 * `before:` so it can coexist with the outermost cell's `after:` gutter cover on
 * a single pinned column that is both outer and edge.
 */
export function edgeFadeClasses(side: "left" | "right"): string {
  return cn(
    "relative before:pointer-events-none before:absolute before:top-0 before:bottom-0 before:w-6 before:bg-inherit before:content-['']",
    side === "left"
      ? "before:left-full before:[mask-image:linear-gradient(to_right,#000,transparent)] before:[-webkit-mask-image:linear-gradient(to_right,#000,transparent)]"
      : "before:right-full before:[mask-image:linear-gradient(to_left,#000,transparent)] before:[-webkit-mask-image:linear-gradient(to_left,#000,transparent)]",
  )
}

/**
 * Classes that paint the `EDGE_GUTTER_PX` strip between an outermost sticky cell
 * and the scroll-container edge. Pinned / selection / action cells sit one
 * gutter in from that edge so their content lines up with the page header
 * (`px-6`); without this, the strip would expose scrolling center cells during
 * horizontal scroll (content visible past a pinned column). A `bg-inherit`
 * pseudo-element fills the strip with the cell's own background, so it tracks
 * the row's hover / selected state and nothing shows through. `w-3` mirrors
 * `EDGE_GUTTER_PX` (12px), the same pairing as `EDGE_GUTTER_CLASS`.
 *
 * It also carries `border-b` so the row/header divider extends across the strip
 * (the cell's own `border-b` stops at the cell edge, and the opaque strip would
 * otherwise hide the container border under it at fractional DPR — see
 * `pinnedCellClasses`). `-bottom-px` drops the strip 1px past the padding box so
 * its border lands on the same row as the cell's border (which sits just below
 * the padding box), keeping the line continuous and 1px thick. `divider: false`
 * drops that border for a row that has none to extend — a platter table's
 * floating header, where it would otherwise read as a stray gutter-width rule
 * beside an undivided header.
 *
 * Width is an explicit `after:w-[12px]` (not `after:w-3`) to exactly span
 * `EDGE_GUTTER_PX`: under the app's `html { font-size: 0.9rem }`, `w-3`
 * (0.75rem ≈ 10.8px) is ~1px narrower than the 12px sticky offset, leaving a
 * sliver where scrolling content peeks through past the pinned cell.
 */
export function gutterCoverClasses(
  side: "left" | "right",
  { divider = true }: { divider?: boolean } = {},
): string {
  return cn(
    "relative after:pointer-events-none after:absolute after:top-0 after:-bottom-px after:w-[var(--table-edge-gutter,12px)] after:bg-inherit after:content-['']",
    divider && "after:border-b after:border-coval-border",
    side === "left" ? "after:right-full" : "after:left-full",
  )
}

/**
 * Reconcile a stored column order against the current default order: keep the
 * stored positions for columns that still exist, drop ids that no longer do,
 * and splice newly-added ids in at their default index. Keeps the uncontrolled
 * order stable across reorders while staying in sync when `columns` changes.
 */
export function reconcileColumnOrder(
  stored: string[],
  fallback: string[],
): string[] {
  const fallbackSet = new Set(fallback)
  const kept = stored.filter((id) => fallbackSet.has(id))
  // Every still-valid id is present (kept ⊆ fallback, equal length) — keep the
  // stored order as-is; nothing was added.
  if (kept.length === fallback.length) {
    return kept
  }
  const result = [...kept]
  fallback.forEach((id, index) => {
    if (!result.includes(id)) {
      result.splice(Math.min(index, result.length), 0, id)
    }
  })
  return result
}

// Bridge the consumer-facing expanded-row-id array and TanStack's ExpandedState
// record (`{ [rowId]: true }`), so expansion mirrors the `columnOrder` /
// `selectedIds` id-array conventions rather than leaking the internal shape.
export function expandedIdsToRecord(
  ids: string[] | undefined,
): Record<string, boolean> {
  const record: Record<string, boolean> = {}
  for (const id of ids ?? []) {
    record[id] = true
  }
  return record
}

export function recordToExpandedIds(record: Record<string, boolean>): string[] {
  return Object.keys(record).filter((id) => record[id])
}
